/**
 * Requests & questions — client followups captured by the voice assistant
 * (V044), worked NEW → CONTACTED → CLOSED (V045 audit columns).
 *
 * All three staff roles can open this screen; the backend scopes rows
 * server-side (agents see only requests routed to them, admins the tenant).
 * Reply posts an answer the client sees on their dashboard; Later defers the
 * request to CONTACTED. Both refresh this list and the header bell count.
 */

import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiClientError } from "@re-platform/api-client";
import { Button } from "@re-platform/ui";

import {
  actingClientFor,
  brokerageLabel,
  useMergedQuery,
} from "../../lib/mergedQuery";
import { Avatar } from "../../lib/cards";
import { useMemberships } from "../../lib/memberships";

export interface Followup {
  id: string;
  client_user_id: string;
  client_name: string | null;
  agent_user_id: string | null;
  agent_name: string | null;
  topic: string;
  details: string;
  preferred_time: string | null;
  status: "NEW" | "CONTACTED" | "CLOSED";
  agent_reply: string | null;
  replied_at: string | null;
  created_at: string;
  contacted_at: string | null;
  closed_at: string | null;
}

const TOPIC_LABEL: Record<string, string> = {
  appointment: "appointment",
  callback: "callback",
  question: "question",
  other: "other",
};

type Filter = "NEW" | "CONTACTED" | "CLOSED" | "all";
const FILTERS: readonly Filter[] = ["NEW", "CONTACTED", "CLOSED", "all"];

function age(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return "<1h";
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function Requests() {
  const [filter, setFilter] = useState<Filter>("NEW");
  const [error, setError] = useState<string | null>(null);
  const [replyOpen, setReplyOpen] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  // Merged across brokerages for dual-licensed staff (PR 5).
  const memberships = useMemberships();
  const tenants = memberships.data ?? [];
  const query = useMergedQuery<Followup>(
    tenants,
    ["followups", filter],
    (client) =>
      client.request<Followup[]>(
        filter === "all"
          ? "/voice-agent/followups"
          : `/voice-agent/followups?status=${filter}`,
      ),
    { enabled: tenants.length > 0 },
  );

  const mutation = useMutation({
    mutationFn: ({
      id,
      status,
      tenantId,
    }: {
      id: string;
      status: "CONTACTED" | "CLOSED";
      tenantId: string;
    }) =>
      actingClientFor(tenantId, query.isDual).request<Followup>(
        `/voice-agent/followups/${id}`,
        { method: "PATCH", body: { status } },
      ),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["followups"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications-summary"] }),
      ]);
    },
    onError: (err) =>
      setError(
        err instanceof ApiClientError
          ? err.message
          : "Could not update the request. Please try again.",
      ),
  });

  const replyMutation = useMutation({
    mutationFn: ({
      id,
      message,
      tenantId,
    }: {
      id: string;
      message: string;
      tenantId: string;
    }) =>
      actingClientFor(tenantId, query.isDual).request<Followup>(
        `/voice-agent/followups/${id}/reply`,
        { method: "POST", body: { message } },
      ),
    onSuccess: async () => {
      setError(null);
      setReplyOpen(null);
      setReplyText("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["followups"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications-summary"] }),
      ]);
    },
    onError: (err) =>
      setError(
        err instanceof ApiClientError
          ? err.message
          : "Could not send reply. Please try again.",
      ),
  });

  function openReply(id: string) {
    setReplyOpen(id);
    setReplyText("");
    setTimeout(() => replyRef.current?.focus(), 50);
  }

  const rows = query.rows;

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0 }}>Requests &amp; questions</h1>
        <p style={{ color: "var(--rp-color-text-muted)", marginTop: 4 }}>
          Questions and callbacks the voice assistant captured for a human —
          reply to the client, or set one aside for later.
        </p>
      </header>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "primary" : "secondary"}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "All" : f.charAt(0) + f.slice(1).toLowerCase()}
          </Button>
        ))}
      </div>

      {error && (
        <p style={{ color: "var(--rp-color-danger, red)", fontSize: 13 }}>
          {error}
        </p>
      )}

      {query.isPending ? (
        <p style={{ color: "var(--rp-color-text-muted)" }}>Loading…</p>
      ) : query.isError ? (
        <p style={{ color: "var(--rp-color-danger, red)", fontSize: 13 }}>
          {query.error instanceof Error
            ? query.error.message
            : String(query.error)}
        </p>
      ) : rows.length === 0 ? (
        <p style={{ color: "var(--rp-color-text-muted)" }}>
          No {filter === "all" ? "" : filter.toLowerCase() + " "}requests.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map(({ row: f, tenant }) => {
            const answered = !!f.agent_reply;
            const closed = f.status === "CLOSED";
            const busy = mutation.isPending || replyMutation.isPending;
            return (
              <div
                key={`${tenant.tenant_id}:${f.id}`}
                style={{
                  border: "1px solid var(--rp-color-border)",
                  // Amber accent flags an unanswered, still-open request.
                  borderLeft:
                    answered || closed
                      ? "1px solid var(--rp-color-border)"
                      : "4px solid var(--rp-color-warning)",
                  borderRadius: "var(--rp-radius-lg)",
                  background: "var(--rp-color-surface)",
                  boxShadow: "var(--rp-shadow-sm)",
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <Avatar name={f.client_name} />
                  <span style={{ fontWeight: 700, fontSize: 15 }}>
                    {f.client_name ?? "Client"}
                  </span>
                  {query.isDual && (
                    <span
                      style={{
                        fontSize: 12.5,
                        color: "var(--rp-color-text-muted)",
                      }}
                    >
                      {brokerageLabel(tenant)}
                    </span>
                  )}
                  {answered && (
                    <span
                      style={{
                        marginLeft: "auto",
                        background: "var(--rp-color-success-faint)",
                        color: "var(--rp-color-success)",
                        fontSize: 11.5,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        padding: "3px 10px",
                        borderRadius: 999,
                      }}
                    >
                      ANSWERED
                    </span>
                  )}
                </div>

                {answered ? (
                  <div
                    style={{
                      marginTop: 6,
                      marginLeft: 44,
                      color: "var(--rp-color-text-muted)",
                      fontSize: 13.5,
                    }}
                  >
                    Replied about {TOPIC_LABEL[f.topic] ?? f.topic}
                    {f.replied_at ? ` · ${age(f.replied_at)} ago` : ""}
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        marginTop: 8,
                        marginLeft: 44,
                        fontSize: 15,
                        color: "var(--rp-color-text)",
                      }}
                    >
                      &ldquo;{f.details}&rdquo;
                    </div>
                    {f.preferred_time && (
                      <div
                        style={{
                          marginTop: 4,
                          marginLeft: 44,
                          fontSize: 12.5,
                          color: "var(--rp-color-text-muted)",
                        }}
                      >
                        Prefers {f.preferred_time}
                      </div>
                    )}

                    {replyOpen === f.id ? (
                      <div style={{ marginTop: 12, marginLeft: 44 }}>
                        <textarea
                          ref={replyRef}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply — the client sees this on their dashboard…"
                          rows={3}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "8px 10px",
                            border: "1px solid var(--rp-color-border)",
                            borderRadius: 8,
                            fontSize: 13.5,
                            resize: "vertical",
                            fontFamily: "inherit",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 8,
                          }}
                        >
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={
                              !replyText.trim() || replyMutation.isPending
                            }
                            onClick={() =>
                              replyMutation.mutate({
                                id: f.id,
                                message: replyText,
                                tenantId: tenant.tenant_id,
                              })
                            }
                          >
                            {replyMutation.isPending
                              ? "Sending…"
                              : "Send reply"}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setReplyOpen(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 12,
                          marginLeft: 44,
                        }}
                      >
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={busy}
                          onClick={() => openReply(f.id)}
                        >
                          Reply
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          title="Set aside — moves to Contacted to follow up later"
                          onClick={() =>
                            mutation.mutate({
                              id: f.id,
                              status: "CONTACTED",
                              tenantId: tenant.tenant_id,
                            })
                          }
                        >
                          Later
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
